"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { X, User, Send, Loader2 } from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import { dictionary } from "../../utils/dictionary"
import { useCancelChat } from "../../hooks/useCancelChat"
import { CancelChatMessage, CancelChatSender } from "../../services/membership/cancelChatService"

interface CancelChatModalProps {
  isOpen: boolean
  onClose: () => void
  onCanCancel?: (lastUserMessage: string | null) => void
  onDownsell?: () => void
  onRecovered?: () => void
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="flex items-start space-x-2 max-w-[80%]">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-200">
          <User className="w-4 h-4 text-gray-600" />
        </div>
        <div className="p-3 rounded-2xl bg-gray-100 text-gray-900">
          <div className="flex space-x-1 items-center h-5">
            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        </div>
      </div>
    </div>
  )
}

export function CancelChatModal({ isOpen, onClose, onCanCancel, onDownsell, onRecovered }: CancelChatModalProps) {
  const { language } = useAuth()
  const [message, setMessage] = useState("")
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const outcomeHandledRef = useRef(false)
  const introPlayedRef = useRef(false)

  const [introMessages, setIntroMessages] = useState<string[]>([])
  const [showTyping, setShowTyping] = useState(false)

  const {
    messages,
    canCancel,
    downsell,
    recovered,
    isLoading,
    sendMessage,
    initializeChat,
    getLastUserMessage,
    clearChat,
    addBotMessage,
  } = useCancelChat(language)

  const camilaGreeting =
    dictionary["hello-my-name-is-camila"]?.[language] ||
    "Hello, my name is Camila, and I'm here to help you with your cancellation request."

  const initialQuestion =
    dictionary["tell-me-what-problems-are-you-finding-with-fluxi"]?.[language] ||
    "Cuéntame, ¿qué problemas estás encontrando con Fluxi que no te permiten aprovechar tu membresía completamente?"

  const playIntroSequence = useCallback(() => {
    if (introPlayedRef.current) return
    introPlayedRef.current = true

    setIntroMessages([])
    setShowTyping(false)

    // 1s delay → show typing
    const t1 = setTimeout(() => {
      setShowTyping(true)
    }, 1000)

    // 3s (1s wait + 2s typing) → show first message, then typing again
    const t2 = setTimeout(() => {
      setShowTyping(false)
      setIntroMessages([camilaGreeting])
      setTimeout(() => setShowTyping(true), 100)
    }, 3000)

    // 5s (3s + 2s typing) → show second message
    const t3 = setTimeout(() => {
      setShowTyping(false)
      setIntroMessages([camilaGreeting, initialQuestion])
    }, 5000)

    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
    }
  }, [camilaGreeting, initialQuestion])

  useEffect(() => {
    if (isOpen) {
      outcomeHandledRef.current = false
      initializeChat()

      // Only play intro if no saved messages
      const saved = messages.length > 0
      if (!saved) {
        introPlayedRef.current = false
        const cleanup = playIntroSequence()
        return cleanup
      }
    } else {
      // Reset intro state when modal closes
      introPlayedRef.current = false
      setIntroMessages([])
      setShowTyping(false)
    }
  }, [isOpen])

  // Handle can_cancel outcome (existing behavior)
  useEffect(() => {
    if (canCancel && onCanCancel && !outcomeHandledRef.current) {
      outcomeHandledRef.current = true
      const lastUserMessage = getLastUserMessage()
      clearChat()
      onCanCancel(lastUserMessage)
    }
  }, [canCancel, onCanCancel, getLastUserMessage, clearChat])

  // Handle downsell outcome
  useEffect(() => {
    if (downsell && onDownsell && !outcomeHandledRef.current) {
      outcomeHandledRef.current = true
      const closingMessage =
        dictionary["downsell-closing-message"]?.[language] ||
        "Te dejo la información sobre el Plan Lite para que puedas proceder a confirmarlo."
      addBotMessage(closingMessage)

      setTimeout(() => {
        clearChat()
        onDownsell()
      }, 5000)
    }
  }, [downsell, onDownsell, language, addBotMessage, clearChat])

  // Handle recovered outcome
  useEffect(() => {
    if (recovered && onRecovered && !outcomeHandledRef.current) {
      outcomeHandledRef.current = true
      const closingMessage =
        dictionary["recovered-closing-message"]?.[language] ||
        "¡Me alegra haber podido ayudarte! Cerraré este chat ahora. Recuerda que siempre puedes reactivar cuando quieras."
      addBotMessage(closingMessage)

      setTimeout(() => {
        clearChat()
        onRecovered()
      }, 2000)
    }
  }, [recovered, onRecovered, language, addBotMessage, clearChat])

  useEffect(() => {
    scrollToBottom()
  }, [messages, isLoading, introMessages, showTyping])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const buildIntroAsChatMessages = (): CancelChatMessage[] => {
    return introMessages.map((text, i) => ({
      id: `intro-${i}`,
      text,
      sender: CancelChatSender.BOT,
      timestamp: Date.now() - (introMessages.length - i) * 1000,
    }))
  }

  const handleSend = async () => {
    if (message.trim() && !isLoading) {
      const messageToSend = message.trim()
      setMessage("")
      const prepend = messages.length === 0 ? buildIntroAsChatMessages() : undefined
      await sendMessage(messageToSend, prepend)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && message.trim() && !isLoading) {
      handleSend()
    }
  }

  const introFinished = messages.length === 0 && introMessages.length === 2 && !showTyping
  const inputDisabled = isLoading || (messages.length === 0 && !introFinished)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-3xl shadow-lg max-w-2xl w-full mx-4 overflow-hidden h-[600px] flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header con gradiente */}
        <div
          className="text-white p-6"
          style={{
            background: "linear-gradient(to right, rgb(80, 80, 246), rgb(133, 162, 255))",
          }}
        >
          <div className="flex items-center space-x-3">
            <div>
              <h3 className="text-lg font-semibold">
                {dictionary["fluxi-support"]?.[language] || "Camila - Cancellation Agent"}
              </h3>
              <p className="text-blue-100 text-sm">
                {dictionary["we-are-here-to-help-you"]?.[language] || "I'm here to help you"}
              </p>
            </div>
          </div>
        </div>

        {/* Área de mensajes */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-6 space-y-4"
        >
          {/* Intro messages (animated sequence) */}
          {messages.length === 0 && introMessages.map((text, index) => (
            <div key={`intro-${index}`} className="flex justify-start">
              <div className="flex items-start space-x-2 max-w-[80%]">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-200">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <div className="p-3 rounded-2xl bg-gray-100 text-gray-900">
                  <p className="text-sm leading-relaxed">{text}</p>
                </div>
              </div>
            </div>
          ))}

          {/* Typing indicator for intro sequence */}
          {messages.length === 0 && showTyping && <TypingIndicator />}

          {/* Mensajes del chat */}
          {messages.map((msg: CancelChatMessage) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`flex items-start space-x-2 max-w-[80%] ${
                  msg.sender === "user" ? "flex-row-reverse space-x-reverse" : ""
                }`}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-200">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <div
                  className={`p-3 rounded-2xl text-sm leading-relaxed ${
                    msg.sender === "user"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-900"
                  }`}
                >
                  <p>{msg.text}</p>
                </div>
              </div>
            </div>
          ))}

          {/* Indicador de carga (API response) */}
          {isLoading && <TypingIndicator />}

          {/* Ref para auto-scroll */}
          <div ref={messagesEndRef} />
        </div>

        {/* Input y botón de enviar */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={inputDisabled}
              placeholder={
                dictionary["write-your-message"]?.[language] || "Escribe tu mensaje..."
              }
              className="flex-1 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <button
              onClick={handleSend}
              disabled={!message.trim() || inputDisabled}
              className={`px-4 py-3 rounded-xl transition-colors ${
                message.trim() && !inputDisabled
                  ? "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
